import { useState } from "react";
import { useOutletContext } from "react-router";

import type { AdminChallenge } from "@/lib/api";

export interface ChallengeContext {
  challenge: AdminChallenge;
  refetch: () => void;
}

export function useChallenge(): ChallengeContext {
  return useOutletContext<ChallengeContext>();
}


/** A `null`-safe "did it change" for building a patch field by field. */
export function changed<T>(before: T, after: T): boolean {
  return JSON.stringify(before) !== JSON.stringify(after);
}

/**
 * Form state seeded from the challenge row and re-seeded when the row changes.
 *
 * Keyed on the JSON of the row rather than its identity, so a refetch that changed nothing
 * does not discard an edit in progress, while a save -- which changes the row -- shows what
 * is now stored. No effect: a stale key simply reads as the freshly seeded value.
 */
export function useChallengeState<T>(
  make: (challenge: AdminChallenge) => T,
): [T, (next: T | ((prev: T) => T)) => void, () => void] {
  const { challenge } = useChallenge();
  const key = JSON.stringify(challenge);
  const [stored, setStored] = useState<{ key: string; value: T }>(() => ({ key, value: make(challenge) }));
  const value = stored.key === key ? stored.value : make(challenge);
  const set = (next: T | ((prev: T) => T)) =>
    setStored((prev) => ({
      key,
      value: typeof next === "function" ? (next as (p: T) => T)(prev.key === key ? prev.value : make(challenge)) : next,
    }));
  const reset = () => setStored({ key, value: make(challenge) });
  return [value, set, reset];
}
